import type { KeyedMutator } from 'swr';

// Lib
import {
    ADD_ERROR,
    ADD_SUCCESS,
    DELETE_ERROR,
    DELETE_FROM_MEDIA_ERROR,
    DELETE_FROM_MEDIA_SUCCESS,
    DELETE_SUCCESS
} from '@/lib/posts/postLabelsAlbumsEvents';

type OptionsType = {
    labelsOnMedia?: { label: string }[];
};

type MediaType = {
    id: string;
    labels: {
        label: string;
    }[];
    albums: {
        id: number;
        count_medias: number;
        name: string;
        type: 'auto' | 'manual';
    }[];
};

type EventType =
    | typeof ADD_SUCCESS
    | typeof ADD_ERROR
    | typeof DELETE_FROM_MEDIA_SUCCESS
    | typeof DELETE_FROM_MEDIA_ERROR
    | typeof DELETE_SUCCESS
    | typeof DELETE_ERROR;

const useLabelsAlbumsOnMedia = ({
    mutateMedias,
    requestParams,
    type,
    albumsParams = {}
}: {
    mutateMedias: KeyedMutator<Record<'medias' | 'stories', MediaType[]>[]>;
    requestParams?: { filter_labels?: string[] };
    type?: 'medias' | 'stories';
    albumsParams?: {
        isAlbum?: boolean;
        albumId?: string;
        album?: { criteria?: { labels?: string[] } };
    };
}) => {
    const currentType = type === 'stories' ? type : 'medias';
    // Function that handle the cache mutation of the medias based on label actions
    const handleLabelsEvent = (
        data,
        event: EventType,
        mediaId: string,
        options: OptionsType = {},
        revalidate = false
    ) => {
        let revalidateMedias = revalidate;
        switch (event) {
            case ADD_SUCCESS:
                return mutateMedias(
                    (prevMedias) =>
                        prevMedias.map((page) => ({
                            ...page,
                            [currentType]: page[currentType].map((element) =>
                                element.id === mediaId
                                    ? {
                                          ...element,
                                          labels: [...(element.labels ?? []), data.slice(-1)[0]]
                                      }
                                    : element
                            )
                        })),
                    revalidateMedias
                );
            case ADD_ERROR:
                return mutateMedias(
                    (prevMedias) =>
                        prevMedias.map((page) => ({
                            ...page,
                            [currentType]: page[currentType].map((element) =>
                                element.id === mediaId
                                    ? {
                                          ...element,
                                          labels: element.labels?.filter(
                                              (el) => el?.label !== data.slice(-1)[0]?.label
                                          )
                                      }
                                    : element
                            )
                        })),
                    revalidateMedias
                );
            case DELETE_FROM_MEDIA_SUCCESS:
            case DELETE_SUCCESS:
                if (albumsParams?.album?.criteria && albumsParams?.album?.criteria.labels) {
                    const newLabelsOnMedia = options?.labelsOnMedia.filter(
                        (el) => el.label !== data.label
                    );
                    if (
                        !newLabelsOnMedia.filter(
                            ({ label }) => albumsParams.album.criteria?.labels?.includes(label)
                        ).length
                    ) {
                        return mutateMedias(
                            (prevMedias) =>
                                prevMedias.map((page) => ({
                                    ...page,
                                    [currentType]: [
                                        ...page[currentType].map((element) =>
                                            element.id === mediaId
                                                ? {
                                                      ...element,
                                                      labels: element?.labels.filter(
                                                          (el) => el.label !== data.label
                                                      )
                                                  }
                                                : element
                                        )
                                    ]
                                })),
                            true
                        );
                    }
                }
                if (requestParams && requestParams?.filter_labels) {
                    const newLabelsOnMedia = options?.labelsOnMedia.filter(
                        (el) => el.label !== data.label
                    );
                    // If the filter label does not contain a label of the current media
                    // We remove this media
                    if (
                        !newLabelsOnMedia.filter(
                            ({ label }) => requestParams.filter_labels?.includes(label)
                        ).length
                    ) {
                        if (event === DELETE_FROM_MEDIA_SUCCESS) {
                            return mutateMedias(
                                (prevMedias) =>
                                    prevMedias.map((page) => ({
                                        ...page,
                                        [currentType]: page[currentType].filter(
                                            (element) => element.id !== mediaId
                                        )
                                    })),
                                true
                            );
                        }
                        revalidateMedias = true;
                    }
                }
                // Remove the label of the current media
                return mutateMedias(
                    (prevMedias) =>
                        prevMedias.map((page) => ({
                            ...page,
                            [currentType]: page[currentType].map((element) =>
                                element.id === mediaId || event === DELETE_SUCCESS
                                    ? {
                                          ...element,
                                          labels: (element.labels ?? []).filter(
                                              (el) => el.label !== data.label
                                          )
                                      }
                                    : element
                            )
                        })),
                    revalidateMedias
                );
            default:
                return null;
        }
    };

    // Function that handle the cache mutation of the medias based on albums actions
    const handleAlbumsEvent = (
        dataAlbum,
        event: EventType,
        mediaId: string,
        revalidate = false
    ) => {
        switch (event) {
            case ADD_SUCCESS:
                return mutateMedias(
                    (prevMedias) =>
                        prevMedias.map((page) => ({
                            ...page,
                            [currentType]: page[currentType].map((element) =>
                                element.id === mediaId
                                    ? {
                                          ...element,
                                          albums: [...(element?.albums ?? []), dataAlbum]
                                      }
                                    : element
                            )
                        })),
                    revalidate
                );
            case ADD_ERROR:
                return mutateMedias(
                    (prevMedias) =>
                        prevMedias.map((page) => ({
                            ...page,
                            [currentType]: page[currentType].map((element) =>
                                element.id === mediaId
                                    ? {
                                          ...element,
                                          albums: element?.albums?.filter(
                                              (el) => el?.id !== dataAlbum.id
                                          )
                                      }
                                    : element
                            )
                        })),
                    revalidate
                );
            case DELETE_SUCCESS:
                if (
                    albumsParams?.isAlbum &&
                    parseInt(albumsParams?.albumId) === parseInt(dataAlbum.id)
                ) {
                    return mutateMedias(
                        (prevMedias) =>
                            prevMedias.map((page) => ({
                                ...page,
                                [currentType]: page[currentType].filter(
                                    (element) => element.id !== mediaId
                                )
                            })),
                        false
                    );
                }

                return mutateMedias(
                    (prevMedias) =>
                        prevMedias.map((page) => ({
                            ...page,
                            [currentType]: page[currentType].map((element) =>
                                element.id === mediaId
                                    ? {
                                          ...element,
                                          albums: element?.albums?.filter(
                                              (el) => el.id !== dataAlbum.id
                                          )
                                      }
                                    : element
                            )
                        })),
                    revalidate
                );
            default:
                return null;
        }
    };

    return { handleAlbumsEvent, handleLabelsEvent };
};

export default useLabelsAlbumsOnMedia;
